import { ImagePaths } from '../../lib/imageUrls.js';

export const copilotSecurityRoadmapEssentialEight = {
  id: 30,
  slug: 'copilot-security-roadmap-essential-eight',
  ogImage: '/og/courses/copilot-security-roadmap-essential-eight.jpg',
  courseCode: 'COPILOT-SEC-E8-001',
  title: 'Microsoft 365 Copilot Security Roadmap: Essential Eight Alignment',
  subtitle: 'ASD Blueprint and E8 controls for Copilot Wave 3',
  valueProposition: 'Map all eight ACSC Essential Eight controls to Microsoft 365 Copilot Wave 3, configure ASD Blueprint ML2 settings with audit evidence, and govern Copilot Studio multi-agent deployments in Australian organisations.',
  duration: '12 hours',
  students: 'New',
  level: 'Advanced',
  organization: 'EDUC4TE',
  image: ImagePaths.COURSE_1,
  shortDescription: 'COPILOT-SEC-E8-001 — Copilot security roadmap covering Essential Eight mapping, ASD Blueprint ML2, and Copilot Studio agent governance for security engineers.',
  featured: false,
  price: {
    aud: 799,
    currency: 'AUD',
    earlyBird: 599
  },
  stripeConfig: {
    priceId: 'price_1TTdVyKu1nyc322UtCnTPnkZ',
    productId: 'prod_USYdRgec1es70x'
  },
  snapshot: {
    whoItsFor: [
      'Security engineers responsible for Essential Eight alignment in Copilot environments',
      'IRAP assessors and security architects reviewing Copilot deployments',
      'CISOs and security leads building the 90-day Copilot security roadmap',
      'IT administrators configuring ASD Blueprint ML2 for Wave 3'
    ],
    accreditation: 'Aligned to ACSC Essential Eight, ASD Blueprint for Secure Cloud, IRAP, ISM, and PSPF'
  },
  logistics: {
    classSize: 'Maximum 16 participants for technical lab exercises',
    requirements: [
      'Completion of Copilot Governance & Compliance course or equivalent security governance experience',
      'Familiarity with ACSC Essential Eight at a conceptual level',
      'Microsoft 365 administrator access for ASD Blueprint configuration exercises',
      'Basic understanding of Copilot Studio is helpful but not required'
    ]
  },
  overview: 'The ACSC Essential Eight was designed before autonomous AI agents existed, but its controls map directly to the security requirements of Microsoft 365 Copilot Wave 3 — and in some cases, Wave 3 introduces new control requirements that go beyond Essential Eight. This advanced course builds the complete 90-day security roadmap that Australian organisations and government agencies need to deploy Copilot Wave 3 in alignment with Essential Eight, the ASD Blueprint for Secure Cloud, and the Information Security Manual.\n\nAcross 12 hours of technical instruction, you will work through all eight Essential Eight controls mapped to Copilot Wave 3 requirements, configure ASD Blueprint ML2 settings with the specific audit evidence that IRAP assessors and internal auditors require, and build the Copilot Studio multi-agent governance framework. The course identifies five additional security controls that are required for Wave 3 but not covered by Essential Eight — including agentic task execution boundaries, Copilot Cowork subprocessor security, Work IQ data minimisation, cross-tenant agent isolation, and AI interaction audit trails.\n\nThe course is structured as a 90-day roadmap: Days 1–30 cover foundational controls, Days 31–60 cover advanced configuration and testing, and Days 61–90 cover audit evidence collection and ongoing monitoring. On completion, participants will have a complete audit evidence pack ready for IRAP assessment and internal governance review.',
  outcomes: [
    'Map all eight ACSC Essential Eight controls to Microsoft 365 Copilot Wave 3 requirements',
    'Configure ASD Blueprint ML2 settings with the audit evidence required by IRAP assessors',
    'Govern Copilot Studio multi-agent deployments using the five controls beyond Essential Eight',
    'Build and execute a 90-day Copilot security roadmap structured around Essential Eight maturity levels',
    'Collect and organise audit evidence that satisfies IRAP, ISM, and PSPF requirements',
    'Assess and document Copilot Studio agent security boundaries and multi-agent orchestration risk',
    'Implement phishing-resistant MFA and privileged access management specific to Copilot agent accounts',
    'Communicate Copilot security posture and remediation progress to executive leadership and auditors'
  ],
  audience: [
    'Security engineers implementing Essential Eight controls in Copilot environments',
    'IRAP assessors and security architects reviewing Wave 3 deployments',
    'CISOs and security leads building 90-day Copilot security roadmaps',
    'IT administrators responsible for ASD Blueprint ML2 configuration',
    'Audit and risk professionals collecting Copilot security evidence'
  ],
  prerequisites: [
    'Completion of Copilot Governance & Compliance for Australian Organisations, or equivalent',
    'Solid understanding of ACSC Essential Eight controls at a conceptual level',
    'Familiarity with Microsoft 365 Defender, Entra ID, and Conditional Access',
    'Experience with Microsoft 365 administration in a production environment'
  ],
  modules: [
    {
      title: 'Module 1: Essential Eight Mapped to Copilot Wave 3',
      duration: '2.5 hours',
      topics: [
        'Application control: governing Copilot Studio agent deployments and plugin execution',
        'Patch applications: Copilot-connected app update cadence and vulnerability surface',
        'Configure Microsoft Office macros: macro policies in Copilot-integrated Office workflows',
        'User application hardening: browser controls and Copilot for web data handling',
        'Restrict administrative privileges: least-privilege for Copilot admin and agent accounts',
        'Patch operating systems: endpoint hardening for Copilot client access',
        'Multi-factor authentication: phishing-resistant MFA for Copilot-licensed users',
        'Regular backups: Copilot-generated content and agent state backup requirements'
      ]
    },
    {
      title: 'Module 2: ASD Blueprint ML2 Configuration',
      duration: '2.5 hours',
      topics: [
        'ASD Blueprint for Secure Cloud: Copilot-specific ML2 configuration requirements',
        'Conditional Access policies: Compliant Device, MFA, and Named Location for Copilot',
        'Entra ID Privileged Identity Management: just-in-time access for Copilot admin roles',
        'Microsoft Defender for Office 365: Safe Links and Safe Attachments in Copilot workflows',
        'Microsoft Purview audit log retention: 12-month retention for Copilot IRAP evidence',
        'Configuration documentation: screenshot evidence format for IRAP assessors'
      ]
    },
    {
      title: 'Module 3: Copilot Studio Multi-Agent Governance',
      duration: '2.5 hours',
      topics: [
        'Copilot Studio architecture: custom agents, connectors, and the agent orchestration layer',
        'Agent isolation: cross-tenant data access prevention and connector scope limitation',
        'Delegated agent permissions: least-privilege OAuth scope design for Copilot Studio agents',
        'Agent testing and red-teaming: identifying prompt injection and data exfiltration vectors',
        'Copilot Studio DLP policies: blocking sensitive data connectors in agent workflows',
        'Agent lifecycle management: deployment approval, monitoring, and retirement controls'
      ]
    },
    {
      title: 'Module 4: Five Controls Beyond Essential Eight',
      duration: '2 hours',
      topics: [
        'Agentic task execution boundaries: scoping autonomous action to approved workflow surfaces',
        'Copilot Cowork subprocessor security: Anthropic data handling assurance and contract requirements',
        'Work IQ data minimisation: limiting behavioural collection to documented business purposes',
        'Cross-tenant agent isolation: preventing agent data leakage across tenant boundaries',
        'AI interaction audit trails: complete logging of agentic task execution for forensic review'
      ]
    },
    {
      title: 'Module 5: 90-Day Roadmap and Audit Evidence',
      duration: '2.5 hours',
      topics: [
        'Days 1-30: foundational controls — identity hardening, Purview baseline, RSR, Copilot Control System',
        'Days 31-60: advanced configuration — Blueprint ML2 settings, Copilot Studio governance, agent DLP',
        'Days 61-90: audit evidence collection — IRAP evidence pack structure and completeness review',
        'Essential Eight Maturity Model self-assessment for Copilot Wave 3 environments',
        'IRAP evidence pack assembly: configuration screenshots, policy documents, and assessment notes',
        'Ongoing monitoring: Copilot security dashboard and monthly compliance review cadence'
      ]
    }
  ],
  format: {
    type: 'Live webinar, self-paced online modules, or lunch-and-learn (12 x 1-hour sessions over 12 days)',
    includes: [
      '12 hours of technical instruction across 5 modules',
      'Essential Eight to Copilot Wave 3 mapping workbook',
      'ASD Blueprint ML2 configuration checklist with audit evidence format',
      'Copilot Studio agent security assessment template',
      '90-day roadmap Gantt template',
      'IRAP evidence pack assembly guide',
      'Access to recorded sessions for 6 months',
      'Certificate of completion',
      'Email support for 30 days post-course'
    ]
  },
  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Live cohort delivery available on request for teams of 5 or more'
  },
  faqs: [
    {
      question: 'Is this course suitable for IRAP assessors?',
      answer: 'Yes. The course maps the Wave 3 control surface to IRAP assessment requirements and documents the ASD Blueprint ML2 configuration evidence format that IRAP assessors use. IRAP assessors will find Modules 2 and 5 particularly relevant for understanding the Copilot-specific assessment scope and evidence requirements.'
    },
    {
      question: 'Does Essential Eight cover all the security requirements for Copilot Wave 3?',
      answer: 'No, and this is a key insight of the course. Essential Eight was designed before autonomous AI agents existed. Wave 3 introduces five additional security requirements that go beyond Essential Eight: agentic task execution boundaries, Cowork subprocessor security, Work IQ data minimisation, cross-tenant agent isolation, and AI interaction audit trails. Module 4 covers all five in detail.'
    },
    {
      question: 'What ASD Blueprint maturity level does this course cover?',
      answer: 'The course targets ML2 (Maturity Level 2) configuration, which is the baseline requirement for Australian Government agencies and the recommended target for regulated private sector organisations deploying Copilot Wave 3. ML2 requires full implementation and active testing of all eight controls. Configuration steps and audit evidence requirements for ML2 are covered in Module 2.'
    },
    {
      question: 'Does this course cover Copilot Studio custom agent security?',
      answer: 'Yes, Module 3 is dedicated to Copilot Studio multi-agent governance. It covers agent isolation, delegated permission design, DLP policies for connectors, agent red-teaming approaches, and lifecycle management controls. This is the most common gap in current Wave 3 deployments and a frequent IRAP finding.'
    },
    {
      question: 'How long does the 90-day roadmap take to execute in practice?',
      answer: 'The roadmap is designed for a dedicated security team running Copilot deployment as an active project. Organisations with existing strong Essential Eight posture can often compress the foundational phase to two to three weeks. Organisations starting from a lower baseline may need longer for the remediation steps before collecting audit evidence. The roadmap template is adaptable to your starting point.'
    },
    {
      question: 'Can we enrol the security team and the Copilot Studio development team together?',
      answer: 'Yes, and this is recommended. Module 3 is directly relevant to Copilot Studio developers and architects, while the rest of the course is primarily for security engineers and administrators. For teams of 5 or more, contact us for a private quote covering combined enrolment.'
    },
    {
      question: 'Will I receive a certificate?',
      answer: 'Yes. Upon completion you will receive a Certificate of Completion from Educ4te confirming your advanced competency in Copilot security roadmap implementation and Essential Eight alignment. This is suitable for IRAP assessor CPD records, LinkedIn, and internal security governance documentation.'
    },
    {
      question: 'Is there a pathway that bundles all four Copilot courses?',
      answer: 'Yes. The Microsoft 365 Copilot Australian Deployment Pathway bundles Foundations Wave 1 & 2, Wave 3 & Agentic AI, Governance & Compliance, and this Security Roadmap course at AUD 2,199 — a saving of AUD 597 versus purchasing individually. Immediate access to all four courses is included.'
    },
    {
      question: 'Can this course be delivered as lunch-and-learn sessions?',
      answer: 'Yes. The course runs as twelve one-hour sessions over twelve days, with the same content and outcomes as the standard format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on exercises are spread across the sessions, so participants need access to a Microsoft 365 tenant throughout. Contact us to arrange a schedule.'
    }
  ]
};
